import { createGroup, createRouter } from '@swan-io/chicane';
import { omit } from 'ramda';

export const domains = {
  dev: 'http://localhost:3030',
  prod: 'https://app.aline.co',
  server: 'http://localhost:8080',
  staging: 'https://staging.aline.co',
} as const;

export const params = {
  /** One-shot deep link to a chat item within the AI panel. */
  aiChatItem: 'aiChatItem',
  categoryId: 'categoryId',
  channelInstall: {
    key: 'channelInstall',
    status: {
      /**
       * Microsoft returned the admin-consent callback without granting the app.
       * Typical when the browser already has a non-admin Microsoft session, so
       * the login page never appears.
       */
      adminRequired: 'admin-required',
      conflict: 'conflict',
      error: 'error',
      pending: 'pending',
      success: 'success',
    },
  },
  /** Carries a `#doc/<id>/ver/<id>/loc/<anchor>` citation across a cross-document navigation. */
  citation: 'citation',
  /** The citation's link text, needed for Apryse text search on the PDF path. */
  citationText: 'citationText',
  /** One-shot deep link that scrolls to / opens a document comment. */
  commentId: 'commentId',
  externalToolkitConnection: {
    completionProof: 'externalToolkitCompletionProof',
    completionState: 'externalToolkitCompletionState',
    key: 'externalToolkitConnection',
    sessionUri: 'session_uri',
    status: {
      authenticationRequired: 'authentication-required',
      error: 'error',
      /** The attempt outlived its browser binding and has to be started again. */
      expired: 'expired',
      success: 'success',
    },
  },
  /**
   * Carries the clicked relationship's ID across a cross-document navigation so the destination
   * document's Related Documents section can highlight that row on arrival.
   */
  highlightRelationship: 'highlightRelationship',
  /** Active Chart within a multi-Chart Insights builder View. */
  insightChart: 'chart',
  integrationsSection: 'integrationsSection',
  /** The task page shown to an internal user as its external assignees see it. */
  previewAsExternal: 'previewAsExternal',
  questionId: 'questionId',
  repositoryFilter: {
    key: 'filter',
    signed: 'signed',
    templates: 'templates',
  },
  /** A document opened from a review task: the editor shows that task's review bar. */
  reviewTaskId: 'taskId',
  rightPanel: {
    editFieldId: 'editFieldId',
    newFieldId: 'newFieldId',
    playbookId: 'playbookId',
  },
  /**
   * One-shot hand-off from the editor's review bar to the task page, which opens the matching
   * decision dialog.
   */
  taskDecision: {
    complete: 'complete',
    key: 'taskDecision',
    remind: 'remind',
    requestChanges: 'requestChanges',
  },
  /**
   * One-shot Insights Chart drill-down onto the Workflows list (View state + bucket filters).
   * Value is URL-encoded JSON (`WorkflowDrilldownPayload`).
   */
  workflowDrilldown: 'workflowDrilldown',
} as const;
export type Params = typeof params;

/**
 * One-shot deep-link query keys that must not persist on an in-app tab's stored route
 * (sessionStorage / history). `walkthrough` and other durable params are intentionally absent.
 */
export const ephemeralRouteParamKeys = [
  params.citation,
  params.citationText,
  params.highlightRelationship,
  params.commentId,
  params.aiChatItem,
  params.integrationsSection,
  params.questionId,
  params.taskDecision.key,
  params.workflowDrilldown,
] as const;

export type EphemeralRouteParamKey = (typeof ephemeralRouteParamKeys)[number];

/**
 * Query keys that stay on a tab while it moves between views of the same resource. Most editor
 * navigations build a bare path (`/doc/abc/comments`), which would otherwise drop them.
 */
export const stickyRouteParamKeys = [params.reviewTaskId] as const;

/**
 * All page routes.
 */
export const pages = {
  ...createGroup('Chat', '/chat', {
    Detail: '/:threadId',
    Index: '/',
  }),
  ...createGroup('Dashboard', '/dashboard', {
    Chat: '/chat',
    ChatDetail: '/chat/:threadId',
    Inbox: '/inbox',
    Index: '/',
  }),
  /**
   * Public, unauthenticated page embedded in the Microsoft Teams task-module
   * iframe for channel-assistant interactions (askUser / workflow intake). Reads
   * a one-time exchange code from its URL fragment and drives the
   * hosted-dialog API; never gated by the app's session check.
   *
   * This is a dedicated, lightweight Vite entry (`channel-interaction.html`), not
   * an SPA route — so the Teams dialog loads a tiny bundle instead of the whole
   * client. The `.html` suffix is intentional: the URL must resolve to the real
   * S3/dev object so it's served directly rather than rewritten to the SPA
   * `index.html`. CloudFront applies the Teams framing carve-out only to this
   * exact `/channel-interaction.html` object.
   */
  ChannelInteraction: '/channel-interaction.html',
  Deactivated: '/deactivated',
  /**
   * Where a provider returns after an external-account consent screen.
   *
   * A bare page on purpose: it is only ever seen inside the small window the
   * connect card opened, and its whole job is to hand control back and close.
   */
  ExternalToolkitConnected: '/integrations/connected',
  GetStarted: '/get-started',
  GrantAccess: '/grant-access',
  Home: '/',
  Login: '/login',
  MagicLinkResent: '/magic-link-resent',
  MagicLinkSent: '/magic-link-sent',
  PreLogin: '/pre-login',
  RequestDocAccess: '/request-doc-access/:documentId',
  Setup: '/setup',
  Signup: '/signup',
  /**
   * Where task emails land. Opens the task beside its first document when it has one, so an
   * external assignee without workflow access still reaches the redline.
   */
  TaskLanding: '/task/:taskId',
  WorkflowDetail: '/workflow/:workflowId',
  WorkflowDoneSigning: '/workflows/:workflowId/done-signing',
  WorkflowReassigned: '/workflows/:workflowId/reassigned',
  /**
   * The workflow detail page's tabs. This group owns every `WorkflowDetailTab*` key: a second
   * group generating the same keys silently wins or loses on spread order, not on path, so the
   * tabs would drift off `/workflow/:workflowId` with nothing failing to type-check.
   */
  ...createGroup('WorkflowDetailTab', '/workflow/:workflowId', {
    Collaborators: '/collaborators',
    Comments: '/comments',
    Documents: '/documents',
    Intake: '/intake',
    Options: '/options',
    PhaseSettings: '/phases/:phaseId/settings',
    Phases: '/phases/:phaseId',
    Steps: '/steps',
    Summary: '/summary',
    Task: '/task/:taskId',
    TaskThread: '/task/:taskId/thread/:threadId',
    Tasks: '/tasks',
    Thread: '/thread/:threadId',
  }),
  WorkflowLaunchConfig: '/launch/:launchConfigId',
  ...createGroup('TemplateSelectedTemplate', '/template/:workflowId', {
    Comments: '/comments',
    Documents: '/documents',
    Index: '/',
    Options: '/options',
    Steps: '/steps',
    Summary: '/summary',
    Tasks: '/tasks',
    Team: '/team',
  }),
  ...createGroup('SendForSignature', '/send-for-signature', {
    Index: '/',
    Workflow: '/:workflowId',
  }),
  ...createGroup('Workflow', '/workflows', {
    All: '/all',
    Board: '/board',
    Completed: '/completed',
    IndexMustRedirect: '/',
    InsightBuilder: '/insights/:viewId',
    Insights: '/insights',
    List: '/list',
    Preview: '/preview/:workflowId',
    ...createGroup('Board', '/board', {
      Settings: '/:boardId/settings',
    }),
  }),
  ...createGroup('Template', '/templates', {
    Draft: '/draft',
    Index: '/',
    Published: '/published',
  }),
  ...createGroup('QuickStart', '/quick-start', {
    ...createGroup('LaunchTemplate', '/template', {
      Index: '/',
      SelectedTemplate: '/:templateWorkflowId',
    }),
    LaunchWorkflow: '/workflow/:workflowId',
    PickWorkflowAction: '/pick-workflow-action/:workflowId',
  }),
  ...createGroup('Repository', '/repository', {
    DocumentPreview: '/documents/:documentId',
    Documents: `/documents`,
    Index: '/',
    Trash: '/trash',
  }),
  ...createGroup('Document', '/doc', {
    Detail: '/:documentId',
    ...createGroup('Detail', '/:documentId', {
      AddInput: '/add-input',
      Ai: '/ai',
      Comments: '/comments',
      Fields: '/fields',
      IntegrationDetail: '/integrations/:integration',
      Integrations: '/integrations',
      Playbooks: '/playbooks',
      Summary: '/summary',
      TrackedChanges: '/track-changes',
      ...createGroup('Version', '/versions', {
        Detail: '/:versionId',
        Index: '/',
      }),
      ...createGroup('Workflow', '/workflow', {
        Detail: '/:workflowId',
        ...createGroup('Detail', '/:workflowId', {
          Fields: '/fields',
        }),
      }),
    }),
  }),
  ...createGroup('Party', '/parties', {
    Index: '/',
    ...createGroup('Detail', '/:partyId', {
      DocumentPreview: '/document/:documentId',
      Documents: '/documents',
      Index: '/',
    }),
  }),
  ...createGroup('Reminders', '/reminders', {
    Detail: '/:reminderId',
    Index: '/',
    Preview: '/preview/:reminderId',
  }),
  ...createGroup('Report', '/reports', {
    Detail: '/:reportId',
    Index: '/',
    ...createGroup('ReportDetail', '/:reportId', {
      Automations: '/automations',
    }),
  }),
  ...createGroup('Playbooks', '/playbooks', {
    Detail: '/:playbookId',
    DetailRule: '/:playbookId/rule/:ruleId',
    Index: '/',
  }),
  ...createGroup('Identifier', '/identifiers', {
    ContractTypes: '/contract-types',
    Index: '/',
    Roles: '/roles',
  }),
  ...createGroup('Admin', '/admin', {
    ApiKeys: '/api-keys',
    Billing: '/billing',
    ContractTypes: '/contract-types',
    DataMigration: '/data-migration',
    Index: '/',
    Integrations: '/integrations',
    Roles: '/roles',
    UpgradeRequests: '/upgrade-requests',
    Usage: '/usage',
    UserProfile: '/profile',
    ...createGroup('Group', '/groups', {
      Detail: '/:groupId',
      Index: '/',
    }),
    ...createGroup('Org', '/org', {
      Index: '/',
    }),
    ...createGroup('User', '/users', {
      Detail: '/:userId',
      Index: '/',
    }),
  }),
  ...createGroup('Community', '/community', {
    Index: '/',
    ...createGroup('Category', `/:${params.categoryId}`, {
      Documents: '/documents',
      Index: '/',
      Workflows: '/workflows',
    }),
    ...createGroup('Workflow', '/workflows', {
      Detail: '/:workflowId',
      Index: '/',
      ...createGroup('DetailTab', '/:workflowId', {
        Steps: '/steps',
        Summary: '/summary',
      }),
    }),
    ...createGroup('Document', '/documents', {
      Detail: '/:documentId',
      Index: '/',
    }),
  }),
} as const;

/**
 * All endpoint routes.
 */
export const endpoints = {
  ServerHealth: '/health',
  Subscriptions: '/subscriptions',
  ...createGroup('Collab', '/collab', {
    ALL: '*',
    Health: '/health',
    Index: '/',
    ...createGroup('Document', '/documents', {
      Detail: '/:document',
      Index: '/',
    }),
  }),
  ...createGroup('Api', '/api', {
    CILogin: '/ci-login',
    CSRF: '/csrf',
    CheckAuth: '/check-auth',
    DocuSign: '/docusign',
    DocumentDownload: '/document-download',
    Envelope: '/envelope',
    GraphQL: '/graphql',
    GuestSessionEnd: '/guest-session/end',
    GuestSessionExchange: '/guest-session/exchange',
    IP: '/ip',
    Index: '/',
    LoginCallback: '/callback',
    LoginRedirect: '/login-redirect',
    Logout: '/logout',
    MagicCallback: '/magic-callback',
    MagicLink: '/magic-link',
    PreLoginRedirect: '/pre-login-redirect',
    SSO: '/sso',
    ...createGroup('Webhooks', '/webhooks', {
      Directory: '/directory',
      FileStorage: '/file-storage',
      Vault: '/vault',
    }),
    ...createGroup('Integrations', '/integrations', {
      /**
       * The channel-assistant interaction form is a public, standalone client
       * page (its own Vite entry — `pages.ChannelInteraction`); it drives its
       * lifecycle (hydrate/uploadFile/deleteFile/roleSearch/submit — see the
       * channel-interaction dialog adapter for the authoritative list) against
       * the shared provider webhook door `Chat/:provider/events` under a
       * reserved provider segment, intercepted before the chat-sdk dispatch.
       */
      ...createGroup('Chat', '/chat', {
        ProviderEvents: '/:provider/events',
        ProviderInstall: '/:provider/install',
        ProviderInstallCallback: '/:provider/install/callback',
      }),
      ...createGroup('Composio', '/composio', {
        CompleteAuth: '/complete-auth',
        Events: '/events',
      }),
    }),
    EnableDirectorySync: '/enable-directory-sync',
    EnableDomainVerification: '/enable-domain-verification',
    SignupRedirect: '/signup-redirect',
    ...createGroup('Salesbricks', '/salesbricks', {
      CompanyCreated: '/company-created',
      CompanyUpdated: '/company-updated',
      InvoicePastDue: '/invoice-past-due',
      InvoicePaymentFailed: '/invoice-payment-failed',
      InvoicePaymentSucceeded: '/invoice-payment-succeeded',
      InvoiceStart: '/invoice-start',
      OrderClosedDelete: '/order-closed-delete',
      OrderClosedLost: '/order-closed-lost',
      OrderComplete: '/order-complete',
      OrderEnd: '/order-end',
      OrderNewRampingPeriod: '/order-new-ramping-period',
      OrderReinstated: '/order-reinstated',
      OrderStageChange: '/order-stage-change',
      OrderTerminated: '/order-terminated',
    }),
  }),
  ...createGroup('Docs', 'https://docs.aline.co/en', {
    Index: '/',
  }),
} as const;

/**
 * All route-paths. Useful for passing to react-router.
 *
 * @example
 *     import {routes} from '@aline/routes';
 *
 *     <Route path={routes.AdminGroupDetail}>  // -> "/admin/groups/:groupId"
 *       <Editor />
 *     </Route>
 */
export const routes = {
  ...pages,
  ...endpoints,
} as const;

/**
 * Pairs of routes that are equivalent for the purpose of getRoutesMatchSameResource.
 */
export const EQUIVALENT_ROUTES: [string, string][] = [
  [routes.RepositoryDocuments, routes.RepositoryIndex],
];

/**
 * Link creation helpers.
 *
 * @example
 *   import { link } from '@aline/routes';
 *
 *   <Link
 *     to={link.AdminUserDetail({ userId: '123' })}
 *   />; // -> "/admin/users/123"
 *
 * @note
 *   The `push`, `replace`, and `useRoute` don't work with react-router, so they are excluded.
 */
export const link = omit(['push', 'replace', 'useRoute'], createRouter(routes));

/**
 * These paths identify content within the `aiChatItem` query parameter; they
 * are not navigable page or API routes. Keep them separate from `routes` so
 * they are not exposed as root `/thread/...` links or matched by React Router.
 */
const aiChatItemRoutes = {
  Citation: '/thread/:threadId/message/:messageId/index/:index',
  Message: '/thread/:threadId/message/:messageId',
  Suggestion: '/thread/:threadId/message/:messageId/group/:groupId',
  Thread: '/thread/:threadId',
} satisfies Record<string, `/${string}`>;
const aiChatItemLink = omit(['push', 'replace', 'useRoute'], createRouter(aiChatItemRoutes));

export function getAiChatCitationItem(options: {
  index: number;
  messageId: string;
  threadId: string;
}) {
  return stripLeadingSlash(aiChatItemLink.Citation({ ...options, index: String(options.index) }));
}

export function getAiChatMessageItem(options: { messageId: string; threadId: string }) {
  return stripLeadingSlash(aiChatItemLink.Message(options));
}

export function getAiChatSuggestionItem(options: {
  groupId: string;
  messageId: string;
  threadId: string;
}) {
  return stripLeadingSlash(aiChatItemLink.Suggestion(options));
}

export function getAiChatThreadItem(options: { threadId: string }) {
  return stripLeadingSlash(aiChatItemLink.Thread(options));
}

export function getDocumentPlaybookRoute(options: { documentId: string; playbookId: string }) {
  const searchParams = new URLSearchParams({
    [params.rightPanel.playbookId]: options.playbookId,
  });
  return `${link.DocumentDetailPlaybooks({ documentId: options.documentId })}?${searchParams}`;
}

export type RepositoryFilterObj = Params['repositoryFilter'];
export type RepositoryFilter = Exclude<
  RepositoryFilterObj[keyof RepositoryFilterObj],
  'repositoryFilter'
>;

export function getRepositoryRoute(options: { filter?: RepositoryFilter } = {}) {
  const { filter } = options;
  const baseRoute = link.RepositoryDocuments();
  return filter ? `${baseRoute}?${params.repositoryFilter.key}=${filter}` : baseRoute;
}

function stripLeadingSlash(path: string) {
  return path.startsWith('/') ? path.slice(1) : path;
}
